/**
 * An intake's history as one list: the backend's event log when the intake
 * has one (older sent intakes fall back to what the timestamps imply; a draft
 * has none), plus the automatic reminders still scheduled. Shared by the Timeline popover and the
 * tracking pane's Activity.
 */
import type {
  OutgoingIntake,
  OutgoingIntakeEventKind,
  OutgoingIntakeSubmission
} from "@/api/types/outgoingIntake";
import { buildAwaitingForeignNationalTimeline } from "@/features/foreignNationalOutgoingIntakes/utils/intakeTimeline";

import { asDate } from "./model";

export type ActivityKind = OutgoingIntakeEventKind | "scheduled_reminder";

export type ActivityItem = {
  id: string;
  kind: ActivityKind;
  at: Date;
  actor: "attorney" | "client" | "system";
  label: string;
  detail?: string;
  scheduled?: boolean;
};

const DAY = 86_400_000;
const text = (v: unknown) => (typeof v === "string" ? v.trim() : "");
const num = (v: unknown) => (typeof v === "number" ? v : Number(v) || 0);
const shortDate = (d: Date | null) =>
  d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";

const describe = (
  kind: OutgoingIntakeEventKind,
  detail: Record<string, unknown>,
  intake: OutgoingIntake
): { label: string; detail?: string } => {
  switch (kind) {
    case "sent":
      return {
        label: "Sent",
        detail: intake.recipient_email
          ? `to ${intake.recipient_email}`
          : undefined
      };
    case "resent":
      return { label: "Sent again with a new link" };
    case "delivered":
      return { label: "Email delivered" };
    case "opened":
      return { label: "Opened the link" };
    case "reminded": {
      const message = text(detail.message);
      return {
        label: detail.auto ? "Automatic reminder" : "Reminder sent",
        detail: message ? `“${message}”` : undefined
      };
    }
    case "bounced":
      return {
        label: "Email bounced",
        detail: text(detail.reason) || undefined
      };
    case "revision_requested": {
      const n = num(detail.count);
      return {
        label: n
          ? `Asked for changes to ${n} ${n === 1 ? "answer" : "answers"}`
          : "Asked for changes"
      };
    }
    case "submitted": {
      const rev = num(detail.revision_number);
      return {
        label: rev > 1 ? `Resubmitted (Submission #${rev})` : "Submitted"
      };
    }
    case "extended":
      return {
        label: `Extended the link to ${shortDate(asDate(detail.expires_at))}`
      };
    case "revoked":
      return { label: "Revoked the link" };
    case "cancelled":
      return { label: "Cancelled the intake" };
    case "link_requested":
      return { label: "Asked for a new link" };
    case "accepted":
      return { label: "Accepted" };
    case "filed": {
      const fields = num(detail.fields);
      const docs = num(detail.documents);
      const parts = [
        fields ? `${fields} ${fields === 1 ? "field" : "fields"}` : null,
        docs ? `${docs} ${docs === 1 ? "document" : "documents"}` : null
      ].filter(Boolean);
      return { label: parts.length ? `Filed ${parts.join(" and ")}` : "Filed" };
    }
    default:
      return { label: String(kind) };
  }
};

const LIVE = new Set(["sent", "in_progress", "revision_requested"]);

/** Automatic reminders that haven't gone out yet. */
export const scheduledReminders = (
  intake: OutgoingIntake,
  now: Date = new Date()
): ActivityItem[] => {
  const schedule = intake.reminder_schedule;
  const sent = asDate(intake.sent_at);
  if (
    !schedule ||
    !sent ||
    !LIVE.has(intake.status) ||
    intake.link_revoked_at ||
    intake.cancelled_at
  )
    return [];
  const expires = asDate(intake.expires_at);
  const out: ActivityItem[] = [];
  for (const d of schedule.days_after_send ?? []) {
    const at = new Date(sent.getTime() + d * DAY);
    if (at > now && (!expires || at < expires))
      out.push({
        id: `scheduled:after:${d}`,
        kind: "scheduled_reminder",
        at,
        actor: "system",
        label: "Automatic reminder",
        scheduled: true
      });
  }
  if (schedule.days_before_expiry && expires) {
    const at = new Date(expires.getTime() - schedule.days_before_expiry * DAY);
    if (at > now)
      out.push({
        id: "scheduled:expiry",
        kind: "scheduled_reminder",
        at,
        actor: "system",
        label: "Automatic reminder",
        detail: "before the link expires",
        scheduled: true
      });
  }
  return out.sort((a, b) => a.at.getTime() - b.at.getTime());
};

export const intakeActivity = (
  intake: OutgoingIntake,
  submissions: OutgoingIntakeSubmission[],
  now: Date = new Date()
): ActivityItem[] => {
  const past: ActivityItem[] = [];
  if (intake.events?.length) {
    intake.events.forEach((e, i) => {
      const at = asDate(e.at);
      if (!at) return;
      const { label, detail } = describe(e.kind, e.detail ?? {}, intake);
      past.push({
        id: `${e.kind}:${i}`,
        kind: e.kind,
        at,
        actor: e.actor,
        label,
        detail
      });
    });
  } else if (intake.status !== "draft") {
    for (const e of buildAwaitingForeignNationalTimeline({
      intake,
      submissions,
      attorneyName: "Firm"
    })) {
      const at = asDate(e.at);
      if (at) {
        past.push({
          id: e.id,
          kind: e.kind,
          at,
          actor: e.actor,
          label:
            e.kind === "sent"
              ? "Sent"
              : e.kind === "opened"
              ? "Opened the link"
              : e.kind === "submitted"
              ? "Submitted"
              : "Asked for changes"
        });
      }
    }
  }
  past.sort((a, b) => a.at.getTime() - b.at.getTime());
  return [...past, ...scheduledReminders(intake, now)];
};
